import { ArrowRight, ShieldCheck, Scale, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import HeroSection from "@/components/HeroSection";
import { TopGuidesSection } from "@/components/TopGuidesSection";
import { useLanguage } from "@/contexts/LanguageContext";
import { DeferredFooter } from "@/components/DeferredFooter";
import { BusinessLoanPage, LoanWithoutUCPage, MortgagePage, PrivateLoanPage } from "@/routes/preloadableRoutes";
import { preloadRoute } from "@/utils/lazyWithPreload";

const trustSignals = [
  { icon: Scale, label: "Oberoende och kostnadsfri" },
  { icon: ShieldCheck, label: "Granskad av redaktionen" },
  { icon: Eye, label: "Tydligt om ersättning" },
];

const HomePathSection = ({ currentLanguage }: { currentLanguage: string }) => {
  const paths = [
    {
      title: "Privatlån",
      description: "Jämför låneförmedlare och långivare.",
      href: `/${currentLanguage}/privatlan`,
      preload: PrivateLoanPage.preload,
    },
    {
      title: "Lån utan UC",
      description: "Se alternativ och risker innan du väljer.",
      href: `/${currentLanguage}/lana-utan-uc`,
      preload: LoanWithoutUCPage.preload,
    },
    {
      title: "Företagslån",
      description: "Hitta finansiering för bolag.",
      href: `/${currentLanguage}/foretagslan`,
      preload: BusinessLoanPage.preload,
    },
    {
      title: "Bolån",
      description: "Räntor, banker och räkneexempel.",
      href: `/${currentLanguage}/bolan`,
      preload: MortgagePage.preload,
    },
  ];

  return (
    <section className="bg-surface py-6 md:py-8">
      <div className="container">
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold md:text-2xl">Välj låneväg</h2>
            <p className="mt-1 text-sm text-muted-foreground">Gå direkt till jämförelsen som matchar ditt behov.</p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {paths.map((item) => (
            <Link
              key={item.title}
              to={item.href}
              className="group rounded-lg border border-border/80 bg-white p-4 transition-colors hover:border-primary/50 hover:bg-primary/5"
              onFocus={() => preloadRoute(item.preload)}
              onMouseEnter={() => preloadRoute(item.preload)}
              onTouchStart={() => preloadRoute(item.preload)}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-foreground">{item.title}</h3>
                  <p className="mt-1 text-sm leading-5 text-muted-foreground">{item.description}</p>
                </div>
                <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const Index = () => {
  const { currentLanguage } = useLanguage();
  const siteUrl = "https://klarfinans.se";
  const localizedBaseUrl = `${siteUrl}/${currentLanguage}`;
  const homePageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        name: "Klarfinans",
        url: siteUrl,
        logo: `${siteUrl}/logo.webp`,
        description: "Klarfinans samlar jämförelser, guider och räkneexempel om lån och privatekonomi.",
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        name: "Klarfinans",
        url: siteUrl,
        inLanguage: "sv-SE",
        publisher: { "@id": `${siteUrl}/#organization` },
      },
      {
        "@type": "WebPage",
        "@id": `${siteUrl}/#webpage`,
        url: siteUrl,
        name: "Jämför lån, bolån och företagslån",
        description: "Jämför lån med tydliga villkor, risker och räkneexempel innan du går vidare.",
        isPartOf: { "@id": `${siteUrl}/#website` },
        about: { "@id": `${siteUrl}/#organization` },
        inLanguage: "sv-SE",
      },
      {
        "@type": "ItemList",
        "@id": `${siteUrl}/#main-paths`,
        name: "Viktiga vägar på Klarfinans",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Bolån", url: `${localizedBaseUrl}/bolan` },
          { "@type": "ListItem", position: 2, name: "Privatlån", url: `${localizedBaseUrl}/privatlan` },
          { "@type": "ListItem", position: 3, name: "Lån utan UC", url: `${localizedBaseUrl}/lana-utan-uc` },
          { "@type": "ListItem", position: 4, name: "Företagslån", url: `${localizedBaseUrl}/foretagslan` },
          { "@type": "ListItem", position: 5, name: "Guider", url: `${localizedBaseUrl}/klarsprak` },
          { "@type": "ListItem", position: 6, name: "Artiklar", url: `${localizedBaseUrl}/blog` },
        ],
      },
    ],
  };

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <SEO
        title="Jämför lån, bolån och företagslån 2026"
        description="Jämför privatlån, bolån, företagslån och lån utan UC med tydliga villkor, räkneexempel och guider. Hitta rätt långivare snabbare med Klarfinans."
        keywords={["jämför lån", "privatlån", "bolån", "företagslån", "lån utan UC", "låneförmedlare", "bolåneräntor", "räkna på lån"]}
        schemaData={homePageSchema}
      />
      <main className="flex-1 bg-surface">
        <HeroSection />

        <section className="border-b border-border/70 bg-background" aria-label="Förtroende och transparens">
          <div className="container">
            <ul className="mx-auto flex max-w-4xl flex-wrap items-center justify-start gap-x-5 gap-y-2 py-3 text-[13px] leading-5 text-muted-foreground sm:justify-center">
              {trustSignals.map(({ icon: Icon, label }) => (
                <li key={label} className="inline-flex min-h-6 items-center gap-1.5 whitespace-nowrap">
                  <Icon className="h-3.5 w-3.5 flex-shrink-0 text-primary/80" aria-hidden="true" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <HomePathSection currentLanguage={currentLanguage} />

        <section className="bg-surface py-6 md:py-8">
          <div className="container">
            <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-xl font-bold md:text-2xl">Lär dig snabbt</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Korta guider för dig som vill förstå villkor innan du går vidare.
                </p>
              </div>
              <Link
                to={`/${currentLanguage}/klarsprak`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              >
                Se alla guider
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <TopGuidesSection />
          </div>
        </section>

        <section className="border-t bg-surface py-8">
          <div className="container">
            <div className="border-l-2 border-primary/50 pl-4 md:flex md:items-center md:justify-between md:gap-6">
              <div>
                <h2 className="text-xl font-bold">Vill du förstå hur vi jobbar?</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Läs om metodik, redaktion och hur jämförelsedata granskas.
                </p>
              </div>
              <Link
                to={`/${currentLanguage}/redaktionen`}
                className="mt-4 inline-flex items-center gap-2 rounded-md border bg-background px-4 py-2 text-sm font-semibold hover:border-primary/50 hover:text-primary md:mt-0"
              >
                Redaktionen
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <DeferredFooter />
    </div>
  );
};

export default Index;
